import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { LayoutChangeEvent } from 'react-native';

import {
  AtomicHeaderType,
  type IScreensComponent,
  type ScreensReadModel,
} from '../../api';
import {
  getScreenComponent,
  convertScreenComponent,
} from '../../api/screensService/getScreenComponent';
import { List, type ListRef } from '@banhala/ui-kit';
import { delay, isNil, isWeb } from '../../utils';
import Animated, {
  runOnJS,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollToTopButton } from './ScrollToTopButton';
import { useLikeMap, useOutfitBookmarkMap } from '../../hooks';
import { createAtomicComponent } from './utils/createAtomicComponent';

export interface AtomicRendererProps {
  screen?: string;
  logDisabled?: boolean;
  listRef: RefObject<ListRef<IScreensComponent> | null>;
  data: ScreensReadModel;
  fetchNextPage: VoidFunction;
  filterListIndex?: number;
  scrollToFilterList: VoidFunction;
  isFetching: boolean;
  refetch: VoidFunction;
  hideScrollToTop?: boolean;
  onScrollY?: (scrollY: number) => void;
  onBeginDrag?: (scrollY: number) => void;
  onEndDrag?: (scrollY: number) => void;
}

const AnimatedList = Animated.createAnimatedComponent(List<IScreensComponent>);

/**
 * @example
<FilterStateProvider>
<AtomicRenderer
  data={data}
  fetchNextPage={fetchNextPage}
/>
</FilterStateProvider>
 */
export const AtomicRenderer = ({
  listRef,
  data,
  fetchNextPage,
  filterListIndex,
  scrollToFilterList,
  isFetching,
  refetch,
  screen,
  hideScrollToTop,
  onScrollY,
  onBeginDrag,
  onEndDrag,
  ...props
}: AtomicRendererProps) => {
  const atomicRendererScrollY = useSharedValue(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const prevContentSizeHeightRef = useRef<number>(0);
  const layoutRef = useRef<{ height: number }>({ height: 0 });
  const { likeMap, setLikeMap } = useLikeMap();
  const { bookmarkMap, setBookmarkMap } = useOutfitBookmarkMap({ initialMap: {} });

  const [componentOverrides, setComponentOverrides] = useState<
    Record<number, IScreensComponent>
  >({});

  useEffect(() => {
    setComponentOverrides({});
  }, [data.components]);

  const effectiveComponents = useMemo(
    () => data.components.map(c => componentOverrides[c.componentId] ?? c),
    [data.components, componentOverrides],
  );

  const handleReloadComponent = useCallback(
    async (componentId: number, carouselSno?: number) => {
      try {
        const rawComponent = await getScreenComponent({
          screenName: data.screenName,
          componentId,
          prevCarousel: carouselSno,
        });
        const converted = convertScreenComponent(rawComponent, data.logging);
        setComponentOverrides(prev => ({
          ...prev,
          [componentId]: converted,
        }));
      } catch {
        // 네트워크 에러 시 무시 — 기존 컴포넌트 유지
      }
    },
    [data.screenName, data.logging],
  );

  const onScrollToIndexFailed = async (info: { index: number }) => {
    await delay(300);
    listRef.current?.scrollToIndex({
      index: info.index,
      animated: false,
    });
  };

  const handleScroll = useAnimatedScrollHandler({
    onScroll: ({ contentSize, layoutMeasurement, contentOffset }) => {
      atomicRendererScrollY.value = contentOffset.y;

      if (onScrollY) {
        runOnJS(onScrollY)(contentOffset.y);
      }

      if (
        isFetching ||
        prevContentSizeHeightRef.current === contentSize.height
      ) {
        return;
      }
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < layoutMeasurement.height * 3) {
        // FlatList가 화면을 그리기전에 scroll을 하면 계속 fetchNextPage를 호출하여 이를 방지.
        // 앱에서만 해당 문제가 발생
        prevContentSizeHeightRef.current = contentSize.height;
        scheduleOnRN(fetchNextPage);
      }
    },
    onBeginDrag: ({ contentOffset }) => {
      if (onBeginDrag) {
        runOnJS(onBeginDrag)(contentOffset.y);
      }
    },
    onEndDrag: ({ contentOffset }) => {
      if (onEndDrag) {
        runOnJS(onEndDrag)(contentOffset.y);
      }
    },
  });

  const onRefresh = () => {
    setIsRefreshing(true);
    // TODO: refetch를 할 때 pagination한만큼의 api를 모두 호출하는 이슈가 있음
    refetch();
    setIsRefreshing(false);
  };

  const onLayout = (e: LayoutChangeEvent) => {
    layoutRef.current.height = e.nativeEvent.layout.height;
  };

  const onContentSizeChange = (_width: number, height: number) => {
    if (isFetching || isNil(data.nextToken)) {
      return;
    }
    // 컨텐츠가 화면보다 작으면 다음 페이지 로드
    if (height < layoutRef.current.height) {
      fetchNextPage();
    }
  };
  return (
    <>
      <AnimatedList
        ref={listRef}
        data={effectiveComponents}
        showsVerticalScrollIndicator={false}
        stickyHeaderIndices={
          isNil(filterListIndex) ? undefined : [filterListIndex]
        }
        onScroll={handleScroll}
        renderItem={({ index, item }) => {
          const onReload =
            item.type.header === AtomicHeaderType.HeadlineReloadOneLine
              ? () =>
                  handleReloadComponent(
                    item.componentId,
                    item.entity.header?.item.carousel_sno,
                  )
              : undefined;

          return createAtomicComponent({
            component: item,
            index,
            likeMap,
            setLikeMap,
            bookmarkMap,
            setBookmarkMap,
            scrollToFilterList,
            screen,
            onReload,
            logDisabled: props.logDisabled,
          });
        }}
        refreshing={isRefreshing}
        onRefresh={onRefresh}
        disableVirtualization={isWeb}
        onScrollToIndexFailed={onScrollToIndexFailed}
        onLayout={onLayout}
        onContentSizeChange={onContentSizeChange}
      />
      {!hideScrollToTop && (
        <ScrollToTopButton
          atomicRendererScrollY={atomicRendererScrollY}
          listRef={listRef}
        />
      )}
    </>
  );
};
